import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import type { ActivityFeedItem } from '../types';

interface ActivityFeedProps {
  items: ActivityFeedItem[] | null;
  degraded: boolean;
  loading: boolean;
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const s = status.toLowerCase();
  if (s === 'success' || s === 'done' || s === 'applied') return 'default';
  if (s === 'pending' || s === 'processing') return 'secondary';
  if (s === 'failed' || s === 'error') return 'destructive';
  return 'outline';
}

export function ActivityFeed({ items, degraded, loading }: ActivityFeedProps) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full rounded" />
        ))}
      </div>
    );
  }

  if (degraded) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground border border-dashed rounded-lg">
        Activity feed unavailable — backend section degraded.
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground border border-dashed rounded-lg">
        No recent activity.
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-36">Time</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead className="text-right tabular-nums">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Rationale</TableHead>
              <TableHead>Agent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                  {formatDateTime(item.ts)}
                </TableCell>
                <TableCell className="text-sm font-medium">{item.action}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  <span className="font-mono">{item.entityType}</span>
                  <span className="ml-1 text-muted-foreground/60">{item.entityId.slice(0, 8)}</span>
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm">
                  {item.amount !== null ? formatCurrency(item.amount) : '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(item.status)} className="text-xs capitalize">
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell
                  className="max-w-xs truncate text-xs text-muted-foreground"
                  title={item.rationale}
                >
                  {item.rationale}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{item.agent}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
